import { IconCheck, IconCreditCardOff, IconGift, IconLoader2, IconSparkles, IconX } from "@tabler/icons-react"
import { type FC, useEffect, useState } from "react"

import {
	type ForentoAiBetaPoolState,
	forentoAiBetaPoolSize,
	getForentoAiBetaPoolState,
} from "@forento/core/models/aiBeta"
import { toNumberFormat } from "@forento/core/utilities/number"
import { cn } from "@forento/ui/utilities/tailwind"

import { useToast } from "~/contexts/ToastContext"
import { useUser } from "~/contexts/UserContext"
import { captureForentoAiEvent, useForentoAiImpression } from "~/utilities/forentoAiAnalytics"
import { query } from "~/utilities/trpc"

const ForentoAiBanner: FC = () => {
	const user = useUser()
	const toast = useToast()
	const [pool, setPool] = useState<ForentoAiBetaPoolState>(() => getForentoAiBetaPoolState())
	const claimCredits = query.ai.claimBetaCredits.useMutation({
		async onSuccess() {
			await user.reload()
		},
		onError(error) {
			console.error(error)
			toast.showError(error.message)
		},
	})

	useForentoAiImpression("forento_ai_banner_viewed", { state: "offer", pool_status: pool.status })

	useEffect(() => {
		const interval = window.setInterval(() => setPool(getForentoAiBetaPoolState()), 1_000)
		return () => window.clearInterval(interval)
	}, [])

	return (
		<section className="overflow-hidden rounded-2xl border border-violet-500/40 bg-[#1d174f] text-white shadow-lg">
			<div className="flex flex-col gap-8 p-6 lg:flex-row lg:items-center lg:justify-between lg:p-7">
				<div className="flex min-w-0 flex-1 flex-col items-start">
					<div className="mb-4 flex size-9 items-center justify-center rounded-lg bg-violet-500 text-white shadow-md">
						<IconSparkles className="size-5" stroke={2.5} />
					</div>
					<h2 className="max-w-xl text-xl leading-tight font-semibold text-balance sm:text-2xl">
						Forento AI is in private beta — claim your 150 free credits
					</h2>
					<ul className="mt-4 flex flex-col gap-2 text-sm text-violet-100 sm:text-[15px]">
						<li className="flex items-start gap-2.5">
							<IconCheck className="mt-0.5 size-4 shrink-0 text-violet-400" stroke={2.5} />
							<span>Generate full courses, quizzes &amp; exams, websites &amp; landing pages</span>
						</li>
						<li className="flex items-start gap-2.5">
							<IconGift className="mt-0.5 size-4 shrink-0 text-violet-400" />
							<span>Celebrate our private beta launch</span>
						</li>
						<li className="flex items-start gap-2.5 font-semibold text-white">
							<IconCreditCardOff className="mt-0.5 size-4 shrink-0 text-violet-400" />
							<span>Free credits — no credit card required</span>
						</li>
					</ul>
					<button
						type="button"
						onClick={() => {
							captureForentoAiEvent("forento_ai_banner_claim_clicked")
							claimCredits.mutate()
						}}
						disabled={pool.status === "refilling" || claimCredits.isPending}
						className="mt-5 inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-violet-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-violet-400 disabled:cursor-auto disabled:bg-slate-600 disabled:text-slate-300"
					>
						{claimCredits.isPending && <IconLoader2 className="size-4 animate-spin" />}
						Claim 150 credits
					</button>
				</div>
				<PoolCounter pool={pool} />
			</div>
		</section>
	)
}

export const ForentoAiClaimedBanner: FC = () => {
	const user = useUser()
	const toast = useToast()
	useForentoAiImpression("forento_ai_banner_viewed", { state: "claimed" })

	const dismissBanner = query.ai.dismissBetaBanner.useMutation({
		async onSuccess() {
			await user.reload()
		},
		onError(error) {
			console.error(error)
			toast.showError("Failed to dismiss the banner. Please try again.")
		},
	})

	return (
		<section className="overflow-hidden rounded-2xl border border-emerald-400/30 bg-[#152f2b] p-6 text-white shadow-lg lg:p-7">
			<div className="flex items-start justify-between gap-4">
				<div className="flex max-w-3xl items-start gap-4">
					<div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-emerald-400 text-emerald-950">
						<IconCheck className="size-6" stroke={3} />
					</div>
					<div>
						<div className="mb-2 text-sm font-semibold text-emerald-300">
							150 credits added to your account
						</div>
						<h2 className="text-xl leading-tight font-semibold sm:text-2xl">
							You're in the Forento AI private beta
						</h2>
						<p className="mt-2 text-sm leading-relaxed text-emerald-50/80 sm:text-[15px]">
							Your credits are live. Start with a course draft, generate a sales page, or build your
							entire website — the smart wizard does the heavy lifting.
						</p>
					</div>
				</div>
				<button
					type="button"
					onClick={() => dismissBanner.mutate()}
					disabled={dismissBanner.isPending}
					aria-label="Dismiss Forento AI banner"
					className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-emerald-100/70 transition-colors hover:bg-white/10 hover:text-white disabled:cursor-auto disabled:opacity-50"
				>
					{dismissBanner.isPending ? (
						<IconLoader2 className="size-4 animate-spin" />
					) : (
						<IconX className="size-5" />
					)}
				</button>
			</div>
		</section>
	)
}

type Props = { pool: ForentoAiBetaPoolState }
const PoolCounter: FC<Props> = ({ pool }) => {
	const progress = (pool.creditsLeft / forentoAiBetaPoolSize) * 100

	return (
		<div className="w-full shrink-0 rounded-xl border border-white/10 bg-[#2b2b2a] p-5 shadow-inner lg:w-[min(42%,360px)]">
			<div className="flex items-center justify-between gap-4">
				<span className="text-sm font-medium text-stone-200">Credits left in the pool</span>
				<span className="flex items-center gap-1.5 text-xs font-medium text-stone-300">
					<span
						className={cn(
							"size-2 rounded-full",
							pool.status === "available" ? "bg-emerald-400" : "bg-stone-500",
						)}
					/>
					{pool.status === "available" ? "live" : "refilling"}
				</span>
			</div>
			<div className="mt-2 text-4xl font-semibold text-violet-500 tabular-nums sm:text-5xl">
				{toNumberFormat(pool.creditsLeft)}
			</div>
			<div className="mt-5 h-2 overflow-hidden rounded-full bg-stone-950/80">
				<div
					className="h-full rounded-full bg-violet-500 transition-[width] duration-500"
					style={{ width: `${progress}%` }}
				/>
			</div>
			<div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-stone-400">
				<span>
					<strong className="font-semibold text-white">{pool.spotsClaimed}</strong> of {pool.totalSpots} spots
					claimed
				</span>
				{pool.status === "refilling" && (
					<span className="font-medium text-stone-200">
						Refills in {formatCountdown(pool.refillSeconds)}…
					</span>
				)}
			</div>
		</div>
	)
}

function formatCountdown(totalSeconds: number): string {
	const minutes = Math.floor(totalSeconds / 60)
	const seconds = totalSeconds % 60
	return `${minutes}:${seconds.toString().padStart(2, "0")}`
}

export default ForentoAiBanner
